import type { ChangeEvent, ReactNode } from "react";
import { useId } from "react";
import { useProfileEditorBrandAccent } from "../../Context/ProfileEditorBrandAccentContext";

export type CheckBoxProps = {
  label?: ReactNode;
  onCheckedChange?: (checked: boolean) => void;
  onClick?: () => void;
  checked: boolean;
  disabled?: boolean;
  titleFirst?: boolean;
  index?: number;
  id?: string;
  name?: string;
  tooltip?: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
};

const shellClasses = (disabled: boolean) =>
  disabled ? "rounded-md" : "";

export default function CheckBox({
  label,
  onCheckedChange,
  onClick,
  checked,
  disabled = false,
  titleFirst = false,
  id,
  name,
  tooltip,
  className = "",
  labelClassName = "",
  inputClassName = "",
}: CheckBoxProps) {
  const generatedId = useId();
  const inputId = id ?? name ?? generatedId;
  const brandAccent = useProfileEditorBrandAccent();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.checked;
    onCheckedChange?.(next);
    onClick?.();
  };

  const accentClass = brandAccent
    ? "text-primary accent-primary"
    : "text-secondary accent-secondary";
  const inputClass = `mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded ${accentClass} disabled:cursor-not-allowed disabled:opacity-50 ${inputClassName}`;

  const hasLabel = label != null && label !== "";

  const control = (
    <input
      id={inputId}
      name={name}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={handleChange}
      className={inputClass}
    />
  );

  return (
    <div className={`flex flex-col ${className}`}>
      <div
        className={`inline-flex max-w-full items-start gap-2.5${shellClasses(
          disabled
        )}`}
      >
        {hasLabel ? (
          <label
            title={tooltip}
            className={`flex max-w-full items-start gap-2.5 select-none ${titleFirst ? "" : "flex-row-reverse"} ${disabled ? "cursor-not-allowed" : `cursor-pointer transition-colors ${brandAccent ? "hover:text-primary" : "hover:text-secondary"}`} ${labelClassName}`}
          >
            <span
              className={`${disabled ? "text-gray-400" : "text-gray-font"}`}
            >
              {label}
            </span>
            {control}
          </label>
        ) : (
          control
        )}
      </div>
    </div>
  );
}
